import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { Annuaire, Annuaires, ColumnReference, FicheDAnnuaire } from '../views/contract.js';
import { cleDeJeu } from '../views/project.js';
import { RAISON_LIENS } from './liens.js';
import type { LiensPort } from './liens.js';
import {
  RAISON_PERSONNES,
  TYPE_ANNUAIRE_PAR_UID,
  estReferenceDePersonnes,
  fichesDAnnuaireDePersonnes,
} from './personnes.js';
import type { PersonneVue, PersonnesPort } from './personnes.js';

/**
 * L'ANNUAIRE D'UN JEU : le titre des fiches qu'une colonne de REFERENCE
 * designe, charge UNE fois pour tout le monde.
 *
 * A QUOI IL SERT. Une colonne de reference stocke un id (« un lien stocke un
 * id, jamais un libelle recopie ») : sans annuaire, la ligne d'une
 * intervention affiche `m1RxQ2...` la ou l'utilisateur attend « Sony FX9 ».
 * L'annuaire est la table de correspondance, et c'est lui qui ferme le fork ou
 * chaque ecran chargeait une collection entiere pour resoudre des noms.
 *
 * UN MAGASIN DE MODULE, comme le cache des modules de vue
 * (`views/useViewModule.ts`), et pour la meme raison : deux ecrans, une carte
 * liee et une fiche peuvent viser le MEME jeu dans le meme document. Un cache
 * par composant chargerait le meme annuaire trois fois, et trois copies de la
 * meme regle de peremption auraient diverge au premier ajustement.
 *
 * IL LIT PAR `list`, JAMAIS PAR `get`. Lire un document ABSENT par `getDoc`
 * rend `permission-denied` et non un snapshot vide (les rules evaluent
 * `resource.data.orgId` sur un `resource` nul, fait deja verifie et ecrit dans
 * `packages/sdk/src/list-query.ts`) : un id orphelin ferait donc remonter une
 * erreur de DROITS, c'est-a-dire le pire message possible pour un fait qui n'en
 * est pas un. Il charge une FENETRE du jeu, sous plafond, et ce qui manque ne
 * revient tout simplement pas.
 *
 * IL NE SE PERIME PAS AU TEMPS. La seule invalidation est le RECHARGEMENT de
 * la porte (`invaliderAnnuaires`, appele par `reloadPort`), donc apres chaque
 * ecriture faite depuis l'ecran. Un cache qui se rafraichit tout seul rendrait
 * le meme ecran different d'une seconde a l'autre sans que rien ne l'explique.
 */

/**
 * LE PLAFOND, ECRIT UNE FOIS. C'est lui qui borne tout le reste : les noms
 * affiches comme la recherche du selecteur.
 *
 * LE SDK NE CONNAIT PAS `in` (une egalite, et rien d'autre :
 * `packages/sdk/src/list-query.ts`), et Firestore borne de toute facon `in` a
 * trente valeurs : un annuaire ne peut donc pas demander « ces quarante ids
 * la ». Il charge une fenetre du jeu entier, et au-dela de ce plafond une fiche
 * existe sans se trouver. C'est une limite assumee, pas un defaut a corriger en
 * douce : Firestore n'a pas de plein texte, et la seule sortie serait un index
 * externe, qui est un autre chantier.
 */
export const PLAFOND_ANNUAIRE = 500;

/** Un annuaire vide, partage : une identite stable evite des rendus pour rien. */
const ANNUAIRE_VIDE: Annuaire = { fiches: new Map(), complet: false };

/**
 * CE QUE LE MAGASIN SAIT D'UN JEU.
 *
 * `revision` EST LA REVISION SOUS LAQUELLE CETTE ENTREE A ETE DEMANDEE, et
 * c'est elle qui tient les deux bords : elle dit qu'une lecture est deja
 * partie pour la revision courante (donc deux hooks montes ensemble ne
 * chargent pas deux fois), et elle fait tomber une reponse PERIMEE, qui ne doit
 * jamais remplacer une fraiche.
 *
 * `complet` NE VIT PLUS ICI, IL VIT DANS L'ANNUAIRE (`Annuaire.complet`) : la
 * projection et la fiche doivent dire la MEME chose d'un id inconnu, et elles
 * ne le peuvent que si le fait voyage avec les fiches, dans le meme objet.
 */
interface EtatAnnuaire {
  revision: number;
  statut: 'loading' | 'ready' | 'error';
  annuaire: Annuaire;
  error?: Error;
}

/** Ce qu'un lecteur a le droit de savoir d'un annuaire, sans son contenu. */
export interface EtatDAnnuaire {
  statut: 'loading' | 'ready' | 'error';
  /** La fenetre chargee couvre-t-elle le jeu entier ? */
  complet: boolean;
  /** La raison de l'echec, quand il y en a un. */
  error?: Error;
}

const MAGASIN = new Map<string, EtatAnnuaire>();
const ABONNES = new Set<() => void>();

/** La revision COURANTE. Elle ne monte que par `invaliderAnnuaires`. */
let REVISION = 0;

/**
 * LA CARTE RENDUE AUX LECTEURS, memoisee.
 *
 * `useSyncExternalStore` compare l'instantane par IDENTITE : en fabriquer une
 * neuve a chaque lecture ferait boucler le rendu. Elle est donc reconstruite au
 * seul moment ou le magasin change, et rendue telle quelle le reste du temps.
 *
 * ELLE NE PORTE QUE CE QUI EST PRET. Une clef absente et un annuaire vide ne
 * disent pas la meme chose (contrat `Annuaires`) : absente, l'annuaire n'est
 * pas encore charge et la cellule montre l'id nu ; presente et vide, il l'est
 * et ne connait pas cet id.
 */
let INSTANTANE: Annuaires = new Map();

function reconstruireInstantane(): void {
  const carte = new Map<string, Annuaire>();
  for (const [cle, etat] of MAGASIN) {
    if (etat.statut === 'ready') carte.set(cle, etat.annuaire);
  }
  INSTANTANE = carte;
}

/**
 * ON PREVIENT APRES AVOIR RECONSTRUIT, et on reconstruit a CHAQUE changement,
 * y compris un echec : l'etat d'un annuaire est lu a cote de son contenu (voir
 * `etatAnnuaire`), et une identite qui ne bouge pas laisserait la fiche
 * afficher « chargement » sur un annuaire deja tombe.
 */
function prevenir(): void {
  reconstruireInstantane();
  for (const abonne of ABONNES) abonne();
}

function sAbonner(abonne: () => void): () => void {
  ABONNES.add(abonne);
  return () => {
    ABONNES.delete(abonne);
  };
}

function lireInstantane(): Annuaires {
  return INSTANTANE;
}

/** La revision courante : ce qui fait repartir les chargements d'un hook monte. */
function lireRevision(): number {
  return REVISION;
}

/**
 * LES ANNUAIRES PRETS, hors React : le meme instantane que le hook rend. Sert au
 * banc d'essai, aux tests, et a tout ce qui projette des lignes sans etre un
 * composant.
 */
export function annuairesCharges(): Annuaires {
  return INSTANTANE;
}

/**
 * TOUS LES ANNUAIRES SONT PERIMES.
 *
 * Appelee par le rechargement de la PORTE (`reloadPort`, `views/useScreen.ts`),
 * donc apres chaque ecriture faite depuis l'ecran, un LOT compris : le nom d'une
 * fiche liee qu'on vient de renommer doit suivre. Elle l'etait par `reload()`
 * jusqu'a la relecture du 09/09/2026, et une modification groupee passait donc
 * a cote (elle recharge sans vider la selection, par `reloadPort`).
 *
 * ELLE NE VIDE PAS LE MAGASIN, elle monte la REVISION : les noms deja resolus
 * restent a l'ecran pendant que la lecture repart. Vider ferait clignoter en
 * ids nus toutes les cellules de reference a chaque rechargement, c'est-a-dire
 * a chaque case modifiee.
 */
export function invaliderAnnuaires(): void {
  REVISION += 1;
  prevenir();
}

/**
 * L'ETAT D'UN ANNUAIRE, lu par sa clef de jeu (`cleDeJeu`).
 *
 * POURQUOI CE N'EST PAS UN HOOK. Son seul lecteur est la FICHE, qui doit dire
 * « Lecture impossible » plutot que de proposer une liste vide (G16), et une
 * vue ne prend que des props : lui passer un hook de plus ferait entrer un
 * contexte dans le contrat des vues. C'est une lecture PURE du magasin, et la
 * fiche est re-rendue de toute facon des que le magasin bouge, puisque la carte
 * des annuaires qu'elle recoit change d'identite au meme instant.
 *
 * `undefined` = personne n'a encore demande cet annuaire.
 */
export function etatAnnuaire(cle: string): EtatDAnnuaire | undefined {
  const etat = MAGASIN.get(cle);
  if (etat === undefined) return undefined;
  return {
    statut: etat.statut,
    complet: etat.annuaire.complet,
    ...(etat.error === undefined ? {} : { error: etat.error }),
  };
}

/**
 * L'ANNUAIRE D'UNE REFERENCE, DEMANDE UNE FOIS PAR REVISION.
 *
 * `titleOf` et `subtitleOf` viennent de la porte REFERENCEE (assembles par
 * `views/screens.ts`) : personne n'invente un second titre, l'annuaire affiche
 * celui que cette porte affiche dans ses propres listes.
 *
 * A UNE PORTE PRES, ET IL FAUT LE DIRE. Le magasin est indexe par JEU, pas par
 * porte : deux portes qui montrent le meme jeu partagent le meme annuaire, et
 * c'est la PREMIERE demande qui fixe le titre pour tout le monde. Un annuaire
 * par porte doublerait la lecture pour deux titres qui, dans la vraie vie, sont
 * les memes ; c'est le prix assume d'un cache partage, et l'assemblage tranche
 * deja dans le meme sens (la premiere porte declaree qui porte un jeu gagne,
 * `liensEntrants`).
 *
 * ELLE EST EXPORTEE, ET C'EST LE MAGASIN SANS REACT : `useAnnuaires` n'est que
 * la liaison React par-dessus (elle abonne, elle rend l'instantane, elle
 * appelle ceci). Les bords qui coutent vraiment (deux hooks qui demandent le
 * meme jeu, une reponse perimee qui revient apres une invalidation, une page
 * pleine, un echec) se tiennent ici, et un test ne peut les atteindre que sans
 * DOM.
 */
export function demanderAnnuaire(
  port: LiensPort | null,
  reference: ColumnReference,
  /**
   * LE PORT DES PERSONNES, pour une reference qui vise leur annuaire
   * (`estReferenceDePersonnes`). Facultatif : les appelants d'avant ce
   * chantier ne demandent que des fiches.
   */
  personnes: PersonnesPort | null = null,
): void {
  /* L'ANNUAIRE DES PERSONNES N'EST PAS UNE FAMILLE DU SDK (comme les membres) :
     il ne se lit pas par `LiensPort.list`, il a son port. Le reste du magasin
     (revision, reponse perimee, echec) est le meme, et c'est pour ca qu'il
     passe par ici plutot que par un second magasin. */
  if (estReferenceDePersonnes(reference)) {
    if (personnes === null) {
      marquerSansPort(reference, SANS_PORT_PERSONNES);
      return;
    }
    demanderAnnuaireDePersonnes(personnes, reference);
    return;
  }
  if (port === null) {
    marquerSansPort(reference, SANS_PORT);
    return;
  }
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  // Deja demande pour CETTE revision : rien a refaire. C'est ce qui empeche
  // deux hooks montes ensemble de charger deux fois le meme jeu.
  if (courant !== undefined && courant.revision === REVISION) return;
  const revision = REVISION;
  MAGASIN.set(cle, {
    revision,
    // On GARDE ce qu'on avait pendant la relecture : le statut ne retombe a
    // `loading` que la toute premiere fois. Sinon les noms disparaitraient a
    // chaque rechargement, le temps d'un aller-retour serveur.
    statut: courant?.statut === 'ready' ? 'ready' : 'loading',
    annuaire: courant?.annuaire ?? ANNUAIRE_VIDE,
  });
  prevenir();

  void port
    .list(reference.family, {
      ...(reference.type === undefined ? {} : { type: reference.type }),
      /* AUCUN `where` : un annuaire resout les noms de TOUTES les fiches qu'une
         colonne peut designer, donc il n'a rien a cadrer d'autre que le
         sous-type. Une egalite y retiendrait justement les fiches deja liees,
         c'est-a-dire l'inverse de ce qu'un choix demande. */
      limit: PLAFOND_ANNUAIRE,
      /* LES ARCHIVES COMPRISES, ET C'EST LE POINT. Une intervention peut
         pointer un materiel archive : sans lui, l'annuaire ne le connaitrait
         pas, et la cellule dirait « Supprimee ou introuvable » sur une fiche qui
         existe toujours. Une archive s'AFFICHE (attenuee, avec son mot) ; ce
         qu'elle ne fait plus, c'est se PROPOSER dans un choix, et ca se decide
         a la surface, pas ici. */
      archived: 'all',
    })
    .then(
      (fiches) => {
        /* UNE REPONSE PERIMEE NE REMPLACE JAMAIS UNE FRAICHE. Entre le depart
           de cette lecture et son retour, une invalidation a pu partir, et sa
           lecture a elle a pu revenir la premiere : ecrire ici reposerait les
           noms d'AVANT l'ecriture qu'on vient de faire. */
        if (MAGASIN.get(cle)?.revision !== revision) return;
        const resolues = new Map<string, FicheDAnnuaire>();
        for (const fiche of fiches) {
          const id = fiche['id'];
          if (typeof id !== 'string' || id === '') continue;
          const subtitle = reference.subtitleOf?.(fiche);
          const type = fiche['type'];
          resolues.set(id, {
            title: reference.titleOf(fiche),
            ...(subtitle === undefined || subtitle === '' ? {} : { subtitle }),
            /* Le sous-type du DOCUMENT, pour qu'une reference sans type sache
               quelle porte typee ouvre cette fiche (`FicheDAnnuaire.type`). */
            ...(typeof type === 'string' && type !== '' ? { type } : {}),
            /* L'ARCHIVE SE LIT SUR LE DOCUMENT, jamais sur une declaration :
               `archivedAt` est le champ standard des trois etats (actif,
               archive, supprime), pose par le SDK (AGENTS.md section 7).
               ET IL NE VAUT JAMAIS POUR UNE SUPPRIMEE : celle-ci porte aussi
               `archivedAt`, donc `archived: 'all'` la ramene du serveur, mais le
               SDK la retire cote client AVANT qu'on arrive ici (`runList`). Un
               annuaire ne contient donc que des fiches vivantes ou archivees, et
               c'est ce qui autorise la cellule a dire « Supprimee ou
               introuvable » de tout id qu'il ne connait pas. */
            archive: fiche['archivedAt'] != null,
          });
        }
        MAGASIN.set(cle, {
          revision,
          statut: 'ready',
          annuaire: {
            fiches: resolues,
            /* « COMPLET » VEUT DIRE « LA PAGE N'EST PAS PLEINE », et c'est le
               seul fait qu'on connaisse vraiment : une page pleine peut cacher
               une fiche de plus, une page a moitie vide, non. Le nombre de
               LIGNES et non la taille de l'annuaire : deux fiches sans id
               tomberaient de la carte et feraient croire a une fenetre non
               pleine. */
            complet: fiches.length < PLAFOND_ANNUAIRE,
          },
        });
        prevenir();
      },
      (cause: unknown) => {
        if (MAGASIN.get(cle)?.revision !== revision) return;
        const error = cause instanceof Error ? cause : new Error(String(cause));
        console.error(`[taki/liens] Annuaire du jeu "${cle}" illisible.`, error);
        /* UN ECHEC LAISSE L'ANNUAIRE VIDE ET `complet` FAUX : rien ne se resout,
           et surtout personne ne conclut « fiche introuvable » sur une lecture
           qui n'a pas eu lieu. La fiche, elle, le DIT (G16). */
        MAGASIN.set(cle, { revision, statut: 'error', annuaire: ANNUAIRE_VIDE, error });
        prevenir();
      },
    );
}

/**
 * LA LECTURE DE L'ANNUAIRE DES PERSONNES, PARTAGEE PAR SES DEUX CLEFS.
 *
 * `personnes` (par `personId`) et `personnes/uid` (par uid, pour la trace)
 * sont deux annuaires du magasin, mais UNE seule collection : un ecran qui
 * montre « Intervenant » et « Cree par » ne la lit qu'une fois par revision
 * et par port. Un port neuf (l'hote a relu la fiche membre) relit.
 */
let LECTURE_PERSONNES: {
  revision: number;
  port: PersonnesPort;
  promesse: Promise<PersonneVue[]>;
} | null = null;

function lirePersonnes(port: PersonnesPort, revision: number): Promise<PersonneVue[]> {
  if (LECTURE_PERSONNES !== null && LECTURE_PERSONNES.revision === revision && LECTURE_PERSONNES.port === port) {
    return LECTURE_PERSONNES.promesse;
  }
  const promesse = port.lister();
  LECTURE_PERSONNES = { revision, port, promesse };
  return promesse;
}

/**
 * L'ANNUAIRE DES PERSONNES, DEMANDE UNE FOIS PAR REVISION : le meme contrat que
 * celui d'une famille (juste au-dessus), servi par le port des personnes.
 *
 * IL EST TOUJOURS COMPLET : le port rend l'annuaire ENTIER (quelques dizaines
 * de personnes, sans plafond), donc un identifiant qu'il ne connait pas n'est
 * a personne, et la case dit `REPLI_PERSONNE_INCONNUE` plutot que l'id.
 */
function demanderAnnuaireDePersonnes(port: PersonnesPort, reference: ColumnReference): void {
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  if (courant !== undefined && courant.revision === REVISION) return;
  const revision = REVISION;
  MAGASIN.set(cle, {
    revision,
    statut: courant?.statut === 'ready' ? 'ready' : 'loading',
    annuaire: courant?.annuaire ?? ANNUAIRE_VIDE,
  });
  prevenir();

  lirePersonnes(port, revision).then(
    (personnes) => {
      if (MAGASIN.get(cle)?.revision !== revision) return;
      MAGASIN.set(cle, {
        revision,
        statut: 'ready',
        annuaire: {
          fiches: fichesDAnnuaireDePersonnes(
            personnes,
            reference.type === TYPE_ANNUAIRE_PAR_UID,
            reference,
          ),
          complet: true,
        },
      });
      prevenir();
    },
    (cause: unknown) => {
      if (MAGASIN.get(cle)?.revision !== revision) return;
      const error = cause instanceof Error ? cause : new Error(String(cause));
      console.error(`[taki/personnes] Annuaire des personnes "${cle}" illisible.`, error);
      MAGASIN.set(cle, { revision, statut: 'error', annuaire: ANNUAIRE_VIDE, error });
      prevenir();
    },
  );
}

/**
 * L'HOTE NE FOURNIT PAS DE PORT : ON LE DIT, on ne fait pas semblant d'attendre.
 *
 * C'est le cas d'un banc d'essai, d'un test ou d'un shell sans SDK. Sans cette
 * marque, l'annuaire resterait « en chargement » POUR TOUJOURS, et le selecteur
 * de fiche liee tournerait sur un squelette que rien n'arrete : un controle mort
 * et muet, exactement ce que G16 interdit. La raison est celle de la carte liee,
 * ecrite une fois (`RAISON_LIENS.sansPort`).
 *
 * `revision: -1` : cette marque n'appartient a AUCUNE revision, donc la premiere
 * vraie demande la remplace. Un hote qui monte son port plus tard n'est pas
 * condamne par un rendu ou il ne l'avait pas encore.
 */
const SANS_PORT = new Error(RAISON_LIENS.sansPort);

/** La meme marque pour l'annuaire des personnes, avec SA raison. */
const SANS_PORT_PERSONNES = new Error(RAISON_PERSONNES.sansPort);

function marquerSansPort(reference: ColumnReference, raison: Error): void {
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  /* ON NE TOUCHE A RIEN DANS TROIS CAS, et le troisieme a ete ajoute apres
     relecture : deja charge par un ecran qui avait un port (un annuaire lu
     reste lisible, meme sur une surface sans port), deja marque, ou UNE LECTURE
     EST EN VOL. Ce dernier cas est reel : une surface sans port montee pendant
     qu'un ecran charge (un banc d'essai a cote d'un ecran, un test) ecrasait la
     demande en cours par une erreur, et la reponse qui revenait ensuite etait
     jetee comme perimee. L'annuaire restait « illisible » pour toujours. */
  if (courant?.statut === 'ready' || courant?.statut === 'loading') return;
  if (courant?.error === raison) return;
  MAGASIN.set(cle, { revision: -1, statut: 'error', annuaire: ANNUAIRE_VIDE, error: raison });
  prevenir();
}

/**
 * LES ANNUAIRES DES REFERENCES DEMANDEES : ce qui est PRET, et rien d'autre.
 *
 * Le hook declenche les chargements qui manquent et rend la carte de ce qui est
 * arrive. Une reference en cours de PREMIER chargement est ABSENTE de la carte,
 * et c'est prevu : la projection montre alors l'id nu, exactement comme les
 * ecrans d'avant ce lot, qui ne passent aucun annuaire.
 *
 * SANS PORT, RIEN NE SE CHARGE ET RIEN NE CASSE : un banc d'essai, un test ou un
 * shell sans SDK montrent des ids, et le selecteur de la fiche DIT pourquoi
 * (`marquerSansPort`). Une app montee sans hote n'est pas cassee, elle est
 * incomplete, et ca se lit.
 */
export function useAnnuaires(
  port: LiensPort | null,
  references: readonly ColumnReference[],
  /**
   * LE PORT DES PERSONNES (`usePersonnesPort()`). TOUS LES APPELANTS LE
   * PASSENT : le moteur d'ecran, la carte liee, le selecteur de rattachement.
   * Une surface qui l'oublierait marquerait l'annuaire des personnes « sans
   * port » et son choix de personne resterait grise, alors que l'hote le
   * fournit. Il n'a pas de valeur par defaut pour que le compilateur le
   * rappelle a chaque appel.
   */
  personnes: PersonnesPort | null,
): Annuaires {
  const annuaires = useSyncExternalStore(sAbonner, lireInstantane, lireInstantane);
  /* LA REVISION EST LUE AU RENDU, et elle est une DEPENDANCE de l'effet :
     `invaliderAnnuaires` previent les abonnes, donc ce composant se rend, donc
     l'effet repart et redemande ce qui vient d'etre perime. Sans elle, une
     invalidation n'aurait recharge que les annuaires dont le CONTENU a change,
     c'est-a-dire aucun. */
  const revision = useSyncExternalStore(sAbonner, lireRevision, lireRevision);
  /* LES CLEFS, ET PAS LE TABLEAU : `references` est refabrique a chaque rendu
     par l'appelant (les colonnes sont assemblees a chaque passage), donc une
     dependance sur son identite relancerait l'effet sans fin. Ce qui decide
     vraiment d'une lecture, ce sont les JEUX vises. */
  const clefs = useMemo(
    () => references.map((reference) => cleDeJeu(reference.family, reference.type)).join('|'),
    [references],
  );
  /* Les references du rendu courant, lues par l'effet sans entrer dans ses
     dependances (voir les clefs juste au-dessus). Meme patron que la carte
     liee avec `carteRef`. */
  const referencesRef = useRef(references);
  referencesRef.current = references;

  useEffect(() => {
    /* Chaque reference part vers SON port, et un port absent marque la
       sienne seulement (`demanderAnnuaire`). */
    for (const reference of referencesRef.current) demanderAnnuaire(port, reference, personnes);
    // `clefs` et `revision` decident ; les references ne sont lues que dedans.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, personnes, clefs, revision]);

  return annuaires;
}

/**
 * LE MAGASIN, REMIS A NEUF. Reserve aux TESTS : deux tests qui partagent un
 * cache de module se contaminent, et le second passerait (ou echouerait) pour
 * une raison ecrite dans le premier.
 */
export function viderAnnuairesPourTest(): void {
  MAGASIN.clear();
  LECTURE_PERSONNES = null;
  REVISION = 0;
  prevenir();
}
